/**
 * REDEX Analytics Utilities
 * Lightweight event tracking wrapper for easy provider swapping
 */

// Event types for REDEX commerce flow
export type RedexEventName =
  | 'redex.page_view'
  | 'redex.cart_add'
  | 'redex.cart_remove'
  | 'redex.upsell_shown'
  | 'redex.upsell_accept'
  | 'redex.upsell_dismiss'
  | 'redex.checkout_start'
  | 'redex.checkout_success'
  | 'redex.checkout_cancel';

export interface RedexEventData {
  skuId?: string;
  skuName?: string;
  bundleId?: string;
  bundleName?: string;
  quantity?: number;
  value?: number;
  currency?: string;
  sessionId?: string;
  orderId?: string;
  items?: Array<{
    skuId: string;
    quantity: number;
    price: number;
  }>;
  [key: string]: unknown;
}

/**
 * Track an analytics event
 * Currently logs to console; swap implementation for GA4, Meta Pixel, etc.
 */
export function trackEvent(eventName: RedexEventName, data?: RedexEventData): void {
  // Development logging
  if (import.meta.env.DEV) {
    console.log(`[Analytics] ${eventName}`, data);
  }

  // Google Analytics 4
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', eventName, {
      ...data,
      send_to: import.meta.env.VITE_GA_MEASUREMENT_ID,
    });
  }

  // Meta Pixel (Facebook)
  if (typeof window !== 'undefined' && (window as any).fbq) {
    // Map to Meta standard events where applicable
    const metaEventMap: Record<string, string> = {
      'redex.cart_add': 'AddToCart',
      'redex.checkout_start': 'InitiateCheckout',
      'redex.checkout_success': 'Purchase',
    };

    const metaEvent = metaEventMap[eventName];
    if (metaEvent) {
      (window as any).fbq('track', metaEvent, {
        content_ids: data?.items?.map(i => i.skuId) || [data?.skuId],
        content_type: 'product',
        value: data?.value ? data.value / 100 : undefined,
        currency: data?.currency || 'USD',
      });
    } else {
      (window as any).fbq('trackCustom', eventName, data);
    }
  }

  // Plausible Analytics
  if (typeof window !== 'undefined' && (window as any).plausible) {
    (window as any).plausible(eventName, { props: data });
  }
}

// ============================================
// CONVENIENCE FUNCTIONS
// ============================================

export function trackCartAdd(skuId: string, skuName: string, quantity: number, value: number): void {
  trackEvent('redex.cart_add', {
    skuId,
    skuName,
    quantity,
    value,
    currency: 'USD',
  });
}

export function trackCartRemove(skuId: string, skuName: string): void {
  trackEvent('redex.cart_remove', {
    skuId,
    skuName,
  });
}

export function trackUpsellShown(skuId: string, skuName: string): void {
  trackEvent('redex.upsell_shown', {
    skuId,
    skuName,
  });
}

export function trackUpsellAccept(skuId: string, skuName: string, value: number): void {
  trackEvent('redex.upsell_accept', {
    skuId,
    skuName,
    value,
    currency: 'USD',
  });
}

export function trackUpsellDismiss(skuId: string, skuName: string): void {
  trackEvent('redex.upsell_dismiss', {
    skuId,
    skuName,
  });
}

export function trackCheckoutStart(
  items: Array<{ skuId: string; quantity: number; price: number }>,
  totalValue: number
): void {
  trackEvent('redex.checkout_start', {
    items,
    value: totalValue,
    currency: 'USD',
  });
}

export function trackCheckoutSuccess(
  sessionId: string,
  orderId: string,
  items: Array<{ skuId: string; quantity: number; price: number }>,
  totalValue: number
): void {
  trackEvent('redex.checkout_success', {
    sessionId,
    orderId,
    items,
    value: totalValue,
    currency: 'USD',
  });
}

export function trackCheckoutCancel(sessionId?: string): void {
  trackEvent('redex.checkout_cancel', {
    sessionId,
  });
}

export function trackPageView(pageName: string): void {
  trackEvent('redex.page_view', {
    page: pageName,
  });
}
